"use client";

import { ArticleModel, ArticleModelFactory } from "@/core/models/article.model";
import { ArticleRepo } from "@/core/repos/tables/article.repo";
import container from "@/lib/di/container";
import { useEffect, useState } from "react";
import {
    FilterBar,
    FilterInputTemplate,
    FilterInputType,
} from "../../components/filter/FilterBar";
import { FilterSearchPayload, DataTable } from "../../components/table/DataTable";
import { DetailModal } from "./modals/DetailModal";
import { Button, message, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTitleDataStore } from "@/core/stores/title-data.store";
import { DeleteButton } from "../../components/table/DeleteButton";
import { formatDatePipe } from "@/core/pipes/format-date.pipe";
import { publishStatusPipe } from "@/core/pipes/publish-status.pipe";

const articleRepo = container.get(ArticleRepo);

export default function ArticlePage() {
    const titleData = useTitleDataStore();
    const [modalData, setModalData] = useState<ArticleModel>(ArticleModelFactory.getEmpty());
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(new Map());
    const [selectedRows, setSelectedRows] = useState<ArticleModel[]>([]);
    const [isDetailModal, setDetailModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [filterTemplates] = useState<FilterInputTemplate[]>([
        {
            dataIndex: "search",
            label: "Tìm kiếm",
            placeholder: "Nhập tiêu đề bản tin",
            type: FilterInputType.text,
        },
        {
            dataIndex: "isPub",
            label: "Trạng thái",
            placeholder: "Chọn trạng thái",
            type: FilterInputType.select,
            options: [
                { name: "Đã xuất bản", value: true },
                { name: "Nháp", value: false },
            ],
        },
    ]);

    useEffect(() => {
        titleData.setTitleActions({
            text: "Thêm bản tin",
            onPress: () => showDetail(ArticleModelFactory.getEmpty()),
        });
    }, []);

    async function onFilterChange(map: Map<string, FilterSearchPayload>) {
        setFilterMap(new Map(map));
    }

    function onRowSelect(rows: ArticleModel[]) {
        setSelectedRows([...rows]);
    }

    const showDetail = (record: ArticleModel) => {
        setModalData(record);
        setDetailModal(true);
    };

    const deleteSelected = async () => {
        setDeleting(true);
        const result = await articleRepo.deleteMany(selectedRows);
        messageApi.success({
            content: `Bạn đã xoá thành công ${result.success}/${result.total} bản ghi`,
        });
        setDeleting(false);
    };

    return (
        <div className="px-4 py-6">
            {contextHolder}
            <FilterBar
                setMap={setFilterMap}
                onChange={onFilterChange}
                templates={filterTemplates}
                map={filterMap}
                actions={[
                    <div key={1} className="mt-6">
                        <DeleteButton
                            loading={deleting}
                            length={selectedRows.length}
                            onConfirm={deleteSelected}
                        />
                    </div>,
                ]}
            />
            <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
                <DataTable<ArticleModel>
                    columns={[
                        { dataIndex: "name", name: "Tiêu đề" },
                        { dataIndex: "urlName", name: "Đường dẫn", width: 200 },
                        { dataIndex: "summary", name: "Tóm tắt", width: 250 },
                        {
                            dataIndex: "publishDate",
                            name: "Ngày xuất bản",
                            width: 130,
                            pipe: formatDatePipe,
                        },
                        {
                            dataIndex: "pubYN",
                            name: "Trạng thái",
                            width: 120,
                            render: (value) => (
                                <Tag color={value ? "green" : "default"}>
                                    {publishStatusPipe(value)}
                                </Tag>
                            ),
                        },
                        {
                            dataIndex: "isHot",
                            name: "Nổi bật",
                            width: 90,
                            render: (value) =>
                                value ? <Tag color="orange">Hot</Tag> : null,
                        },
                        {
                            name: "Thao tác",
                            width: 80,
                            render: (_, record) => (
                                <div className="flex flex-row px-1 items-center justify-center">
                                    <Tooltip title="Sửa bản tin">
                                        <Button
                                            type="text"
                                            icon={<InfoCircleOutlined />}
                                            onClick={() => showDetail(record)}
                                        />
                                    </Tooltip>
                                </div>
                            ),
                            fixed: "right",
                        },
                    ]}
                    repository={articleRepo}
                    filterMap={filterMap}
                    onSelect={onRowSelect}
                />
            </div>
            <DetailModal
                isModalOpen={isDetailModal}
                setIsModalOpen={setDetailModal}
                data={modalData}
            />
        </div>
    );
}
